/**
 * Phase 22 iter_173 — Membership success page (post-Stripe).
 *
 * Polls /memberships/checkout/status/:session_id until payment_status='paid',
 * which activates the membership idempotently. Shows Founder number if
 * assigned (or a friendly retry for still-processing subscriptions).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SharedNavigation from "@/components/SharedNavigation";
import api from "@/lib/api";

const MAX_POLLS = 10;
const POLL_MS = 2000;

const MembershipSuccessPage = () => {
  const [state, setState] = useState({ phase: "polling", info: null });
  const attempts = useRef(0);
  const timer = useRef(null);

  const doPoll = useCallback(async (sessionId) => {
    try {
      const r = await api.get(`/memberships/checkout/status/${sessionId}`);
      const d = r.data || {};
      if (d.payment_status === "paid") {
        setState({ phase: "paid", info: d });
        // iter_202 — Cache invalidation: fire an event so
        // MembershipPage / Profile / Badges refetch without a hard
        // refresh, and preload /memberships/me so the next page load
        // renders the fresh active plan immediately.
        try {
          window.dispatchEvent(new CustomEvent("rgm.membership.updated"));
          api.get("/memberships/me").catch(() => {});
        } catch { /* noop */ }
        return;
      }
      if (d.session_status === "expired") {
        setState({ phase: "expired", info: d });
        return;
      }
      attempts.current += 1;
      if (attempts.current >= MAX_POLLS) {
        setState({ phase: "timeout", info: d });
        return;
      }
      timer.current = setTimeout(() => doPoll(sessionId), POLL_MS);
    } catch (e) {
      setState({ phase: "error", info: { detail: e?.response?.data?.detail } });
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState({ phase: "no_session", info: null });
      return;
    }
    doPoll(sessionId);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [doPoll]);

  const txn = state.info?.transaction;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="membership-success-page">
      <SharedNavigation />
      <div className="max-w-2xl mx-auto px-4 py-12">
        {state.phase === "polling" && (
          <div className="text-center" data-testid="membership-success-polling">
            <p className="text-4xl mb-2">💳</p>
            <p className="text-white font-black">Confirming your payment…</p>
            <p className="text-gray-400 text-sm mt-1">This usually takes a few seconds.</p>
          </div>
        )}
        {state.phase === "paid" && (
          <div
            className="rounded-2xl border-2 border-amber-500/50 bg-gradient-to-br from-amber-900/25 via-purple-900/20 to-black p-8 text-center space-y-4"
            data-testid="membership-success-paid"
          >
            <p className="text-5xl">🏆</p>
            <h1 className="text-3xl font-black">You're in.</h1>
            <p className="text-gray-200 text-sm sm:text-base">
              Your membership is active. Welcome to the next level of RGM.
            </p>
            {txn?.plan_id === "founding_artist" && (
              <p className="text-amber-100 font-black text-lg" data-testid="membership-success-founder-note">
                Check your notifications for your permanent Founder number.
              </p>
            )}
            <Link
              to="/dashboard"
              className="inline-block px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-400 text-white font-black text-sm"
              data-testid="membership-success-continue"
            >
              Go to my dashboard →
            </Link>
          </div>
        )}
        {state.phase === "timeout" && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center" data-testid="membership-success-timeout">
            <p className="text-3xl mb-2">⏱</p>
            <p className="text-white font-black">Still confirming with Stripe…</p>
            <p className="text-gray-400 text-sm mt-1">
              Payments usually settle within a minute. Refresh this page or check your notifications shortly.
            </p>
          </div>
        )}
        {state.phase === "expired" && (
          <div className="rounded-2xl border border-rose-500/40 bg-rose-500/[0.05] p-6 text-center" data-testid="membership-success-expired">
            <p className="text-3xl mb-2">🚫</p>
            <p className="text-white font-black">This checkout session expired.</p>
            <Link to="/membership" className="text-purple-300 underline">Return to membership page</Link>
          </div>
        )}
        {state.phase === "error" && (
          <div className="rounded-2xl border border-rose-500/40 bg-rose-500/[0.05] p-6 text-center" data-testid="membership-success-error">
            <p className="text-3xl mb-2">⚠</p>
            <p className="text-white font-black">Could not confirm your payment.</p>
            <p className="text-gray-400 text-sm mt-1">{state.info?.detail || "Please contact support."}</p>
          </div>
        )}
        {state.phase === "no_session" && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center" data-testid="membership-success-no-session">
            <p className="text-white">No session ID in URL.</p>
            <Link to="/membership" className="text-purple-300 underline">Return to membership page</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default MembershipSuccessPage;
